
import { supabase } from "@/integrations/supabase/client";
import { Subscription } from "@/types/subscription";
import { mapSupabaseDataToSubscription } from "@/utils/dbUtils";

export const fetchSubscriptionsFromSupabase = async (): Promise<Subscription[]> => {
  console.log("fetchSubscriptionsFromSupabase: Starting fetch");
  
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error("fetchSubscriptionsFromSupabase: Error fetching subscriptions:", error);
    throw error;
  }

  console.log("fetchSubscriptionsFromSupabase: Raw data from Supabase:", data);
  console.log("fetchSubscriptionsFromSupabase: Business subscriptions in raw data:", 
    data?.filter(sub => sub.is_business === true));

  if (!data) {
    console.log("fetchSubscriptionsFromSupabase: No data returned");
    return [];
  }

  const mappedSubscriptions = data.map(mapSupabaseDataToSubscription);
  console.log("fetchSubscriptionsFromSupabase: Mapped subscriptions:", mappedSubscriptions);
  console.log("fetchSubscriptionsFromSupabase: Business subscriptions after mapping:", 
    mappedSubscriptions.filter(sub => sub.isBusiness === true));
  
  return mappedSubscriptions;
};

export const createSubscriptionInSupabase = async (subscriptionData: any): Promise<Subscription> => {
  console.log("=== CREATE SUBSCRIPTION IN SUPABASE - DETAILED DEBUG ===");
  console.log("Input subscription data:", JSON.stringify(subscriptionData, null, 2));
  
  // Check if we have proper authentication
  const { data: authData, error: authError } = await supabase.auth.getUser();
  console.log("Authentication check:", { 
    hasUser: !!authData?.user, 
    userEmail: authData?.user?.email,
    authError: authError?.message 
  });
  
  // Verify business fields specifically
  console.log("Business field verification before insert:", {
    is_business: subscriptionData.is_business,
    is_business_type: typeof subscriptionData.is_business,
    business_name: subscriptionData.business_name,
    business_name_type: typeof subscriptionData.business_name,
    business_name_length: subscriptionData.business_name ? subscriptionData.business_name.length : 'N/A'
  });

  // Check if business_name is empty string and convert to null
  if (subscriptionData.is_business && (!subscriptionData.business_name || subscriptionData.business_name.trim() === '')) {
    console.warn("WARNING: Business subscription has empty business_name, this will cause issues!");
    console.log("Setting business_name to null for empty value");
    subscriptionData.business_name = null;
  }

  // Test if we can access the subscriptions table
  console.log("Testing table access...");
  const { data: testData, error: testError } = await supabase
    .from('subscriptions')
    .select('count')
    .limit(1);
  
  if (testError) {
    console.error("Table access test failed:", testError);
    throw new Error(`Cannot access subscriptions table: ${testError.message}`);
  }
  
  console.log("Table access test successful:", testData);
  console.log("Final data being sent to Supabase:", JSON.stringify(subscriptionData, null, 2));
  
  const { data, error } = await supabase
    .from('subscriptions')
    .insert(subscriptionData)
    .select()
    .single();

  console.log("=== SUPABASE INSERT RESPONSE ===");
  if (error) {
    console.error("createSubscriptionInSupabase: Error creating subscription:", error);
    console.log("Error details:", {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code
    });
    
    // Additional debugging for permission issues
    if (error.code === '42501' || error.message.includes('permission')) {
      console.error("PERMISSION ERROR: This might be due to RLS policies or authentication issues");
      console.log("Current user session:", authData?.user?.id);
      console.log("Subscription data that failed:", subscriptionData);
    }
    
    throw error;
  }

  console.log("Raw response from Supabase insert:", JSON.stringify(data, null, 2));
  console.log("Business fields in response:", {
    is_business: data.is_business,
    business_name: data.business_name,
    is_business_type: typeof data.is_business,
    business_name_type: typeof data.business_name
  });

  const mappedSubscription = mapSupabaseDataToSubscription(data);
  console.log("Final mapped subscription:", JSON.stringify(mappedSubscription, null, 2));
  console.log("Business fields in final result:", {
    isBusiness: mappedSubscription.isBusiness,
    businessName: mappedSubscription.businessName
  });
  console.log("=== END CREATE SUBSCRIPTION IN SUPABASE DEBUG ===");
  
  return mappedSubscription;
};
